import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError, from, switchMap } from 'rxjs';

import { CredentialVaultService } from './credential-vault.service';
import { AppLockService } from '../../../services/app-lock.service';
import { AuthStore } from '../store/auth.store';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const vault = inject(CredentialVaultService);
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const appLockService = inject(AppLockService);

  const url = req.url.toLowerCase();

  // Skip token for public auth endpoints
  if (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/register') ||
    url.includes('/api/auth/refresh-token') ||
    url.includes('/api/auth/send-password-reset') ||
    url.includes('/api/auth/reset-password') ||
    url.includes('/api/auth/verify-email') ||
    url.includes('/api/auth/send-verification')
  ) {
    return next(req);
  }

  // Skip token for public payment flow endpoints (ApiKey only)
  if (isPublicPaymentFlowEndpoint(url)) {
    return next(req);
  }

  // Get token asynchronously and add to request
  return from(vault.getToken()).pipe(
    switchMap((token) => {
      if (!token?.accessToken) {
        return next(req);
      }

      // Add token for API requests
      if (url.includes('/api/')) {
        const authReq = addTokenToRequest(req, token.accessToken);
        return next(authReq).pipe(
          catchError((error: unknown) => {
            if (isHttpErrorResponse(error)) {
              if (error.status === 401) {
                // Suppress navigation if lock screen is active — lock screen owns the flow
                if (!appLockService.isLocked()) {
                  void authStore.logout();
                  void router.navigate(['/auth/session-expired']);
                }
                return throwError(() => error);
              }
              if (error.status === 403) {
                void router.navigate(['/auth/access-denied']);
              }
            }
            return throwError(() => error);
          }),
        );
      }

      return next(req);
    }),
  );
};

/**
 * Public payment flow endpoints that only require ApiKey, not Bearer token.
 * Matches: /api/v2/establishments/{id}, /api/v2/payment-links/{id},
 * /api/v2/settings/currency/{code}
 * Does NOT match authenticated endpoints like /api/v2/establishments/show-in-app
 */
function isPublicPaymentFlowEndpoint(url: string): boolean {
  return (
    /\/api\/v2\/establishments\/\d+$/.test(url) ||
    /\/api\/v2\/payment-links\/[a-f0-9-]+$/i.test(url) ||
    /\/api\/v2\/settings\/currency\/[a-z]{3}$/i.test(url)
  );
}

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

function addTokenToRequest(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
